import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { withTypes, FormProps } from "react-final-form";
import Alert from "@mui/material/Alert";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import {
  Grid,
  Button,
  DialogContent,
  DialogActions,
  TextField,
  Theme,
} from "@mui/material";

import Field from "components/Field";
import {
  SkuFormat,
  SkuFormatSaveRequest,
} from "services/KnowfullyAdminClient";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const { Form } = withTypes<SkuFormatSaveRequest>();

type SkuFormatFormProps = {
  skuFormat?: SkuFormat;
  submitLabel: string;
  onSubmit: (values: SkuFormatSaveRequest) => void;
  onCancel?: () => void;
};

const SkuFormatForm = ({
  skuFormat,
  submitLabel,
  onCancel,
  onSubmit,
}: SkuFormatFormProps & FormProps<SkuFormatSaveRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const init = useMemo(() => skuFormat ?? {}, [skuFormat]);

  return useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="nameText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="acronym" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "skuFormatAcronymLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="skuFormatAcronymText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        multiline={true}
                        rows={3}
                        fullWidth
                        label={t({
                          id: "descriptionLabel",
                        })}
                        InputLabelProps={{ required: false }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="descriptionText"
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                disabled={submitting}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="submitButton"
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    ),
    [styles.feedback, init, onCancel, onSubmit, submitLabel, t]
  );
};

export default SkuFormatForm;
